import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Table, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { skillService, type SkillDef } from "@/services/skill";
import { useCardExtra } from "@/layouts/ManagementLayout";
import { formatDateTime } from "@/utils/date";

export function SkillManagement() {
  const { t } = useTranslation("management");
  const { context, hasPermission, permissions } = useManagementStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const canManage = hasPermission("OrgRoleManage") || context?.type === "org";

  const [skills, setSkills] = useState<SkillDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<SkillDef | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      setSkills(await skillService.list(orgId));
    } catch { setSkills([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  useCardExtra(
    canManage ? (
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => {
        setEditingSkill(null);
        form.resetFields();
        setModalOpen(true);
      }}>
        {t("skills.create")}
      </Button>
    ) : null,
    [t, permissions],
  );

  const openEdit = (skill: SkillDef) => {
    setEditingSkill(skill);
    form.setFieldsValue({
      name: skill.name,
      description: skill.description,
      content: skill.content,
      triggers: (skill.triggers || []).join(", "),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name,
      description: values.description || "",
      content: values.content,
      triggers: values.triggers
        ? values.triggers.split(",").map((s: string) => s.trim()).filter(Boolean)
        : [],
    };
    setSaving(true);
    try {
      if (editingSkill) {
        await skillService.update(orgId, editingSkill.id, payload);
      } else {
        await skillService.create(orgId, payload);
      }
      message.success(t("common:success"));
      setModalOpen(false);
      load();
    } catch (e) { message.error(e instanceof Error ? e.message : t("common:error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await skillService.delete(orgId, id);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
  };

  const columns = [
    { title: t("skills.table.name"), dataIndex: "name", key: "name" },
    { title: t("skills.table.description"), dataIndex: "description", key: "description", ellipsis: true },
    {
      title: t("skills.table.created_at"), dataIndex: "created_at", key: "created_at",
      render: (v: string) => formatDateTime(v),
    },
    ...(canManage ? [{
      title: t("common:action"), key: "action", width: 100,
      render: (_: unknown, record: SkillDef) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title={t("skills.delete.confirm")} onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    }] : []),
  ];

  return (
    <>
      <Table dataSource={skills} columns={columns} rowKey="id" loading={loading} size="middle" />
      <Modal
        title={editingSkill ? t("common:edit") : t("skills.create")}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t("skills.table.name")} name="name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Code Review" />
          </Form.Item>
          <Form.Item label={t("skills.table.description")} name="description">
            <Input placeholder={t("skills.form.description_hint")} />
          </Form.Item>
          <Form.Item label={t("skills.form.content")} name="content" rules={[{ required: true }]}>
            <Input.TextArea rows={6} placeholder={t("skills.form.content_hint")} />
          </Form.Item>
          <Form.Item label={t("skills.form.triggers")} name="triggers">
            <Input placeholder={t("skills.form.triggers_hint")} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
